import { useAssignTable } from '@shared/components/assign-table';
import { EmployeeAssignTableDataDto } from '@interfaces/models';
import { getAssignEmployees } from '@api/employees';
import { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaginationState } from '@ui/components';
import { getTableRequest } from '@shared/utils';
import { useGetEmployees } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { useDebouncedState } from '@ui/hooks';

import { getEmployeesColumns } from './vehicle-assign-employees-table-columns';

interface EmployeesTableState {
  pagination: PaginationState;
  globalFilter: string;
}

const employeesTableInitialState: EmployeesTableState = {
  pagination: {
    pageIndex: 0,
    pageSize: 4,
  },
  globalFilter: '',
};

export const useVehicleAssignEmployees = (assignedEmployees: string[]) => {
  const { t } = useTranslation();
  const columns = useMemo(() => getEmployeesColumns(t), [t]);
  const [employeeSearch, setEmployeeSearch] = useDebouncedState<string>('', 800);
  const [state, setState] = useState<EmployeesTableState>(employeesTableInitialState);
  const { employeesOptions, isEmployeesFetching } = useGetEmployees(employeeSearch);
  const totalItemsRef = useRef(0);

  const request = useMemo(() => {
    return getTableRequest({
      pagination: state.pagination,
      search: state.globalFilter,
    });
  }, [state]);

  const {
    data: selectedEmployees,
    isLoading: isSelectedEmployeesLoading,
    isFetching: isSelectedEmployeesFetching,
  } = useQuery({
    queryKey: ['vehicle-employees-table', request, assignedEmployees],
    queryFn: () => getAssignEmployees({ ...request, ids: assignedEmployees }),
  });

  const totalItems = useMemo(() => {
    if (!isSelectedEmployeesFetching) {
      totalItemsRef.current = selectedEmployees?.pagination.totalItems ?? 0;
    }
    return totalItemsRef.current;
  }, [selectedEmployees, isSelectedEmployeesFetching]);

  const assignTable = useAssignTable<EmployeeAssignTableDataDto>({
    isLoading: isSelectedEmployeesLoading && isSelectedEmployeesFetching,
    data: selectedEmployees?.data ?? [],
    phraseChange: setEmployeeSearch,
    getRowId: (row) => row.id,
    options: employeesOptions,
    onStateChange: (state) => {
      setState(state as EmployeesTableState);
    },
    isOptionLoading: isEmployeesFetching,
    initialState: state,
    totalItems,
    columns,
  });

  return assignTable;
};
